'use client';

import { memo, type FC, type MouseEvent } from 'react';
import Link from 'next/link';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import translations from './LegalSection.translations';
import { tmaActions } from '../../../bootstrap/tmaActions';
import { useHost } from '../../../runtime/RuntimeContext';
import { useCapability } from '../../../runtime/useCapability';
import { isCsShotUzHost } from '../../../runtime/host';
import { getLegalLinks } from '../../../utils/legalLinks';
import { useLocalizedHref } from '../../../hooks/useLocalizedHref';
import { isClosedForHost } from '../../../seo/indexability';
import { routeIndexedLanguages } from '../../../seo/routeSeo';

const linkClassName =
  'text-label-tertiary text-[10px] font-normal cursor-pointer no-underline transition-colors duration-200 ease-smooth hover:text-label-secondary active:text-label-secondary';

type TranslationKey = keyof (typeof translations)['en'];

const SITE_PAGES: ReadonlyArray<{ path: string; label: TranslationKey }> = [
  { path: '/how-it-works', label: 'howItWorks' },
  { path: '/contacts', label: 'contacts' },
  { path: '/support', label: 'support' },
  { path: '/refund-terms', label: 'refundTerms' },
  { path: '/digital-content', label: 'digitalContent' },
];

const LegalSectionComponent: FC = () => {
  const { t, lang } = useLocalTranslation(translations);
  const host = useHost();
  const links = getLegalLinks(host);
  const isUz = isCsShotUzHost(host);
  const siteFooter = useCapability('siteFooter');
  const localize = useLocalizedHref();

  const documents: ReadonlyArray<{ url: string; label: TranslationKey }> = [
    { url: links.userAgreement, label: 'userAgreement' },
    { url: links.privacyPolicy, label: 'privacyPolicy' },
    ...(links.refundPolicy ? [{ url: links.refundPolicy, label: 'refundPolicy' as const }] : []),
    ...(links.paymentOrder ? [{ url: links.paymentOrder, label: 'paymentOrder' as const }] : []),
  ];

  // WHY: в Mini App внешнюю ссылку открывает Telegram, а не WebView — иначе документ подменит само приложение
  const openInTelegram = (url: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    void tmaActions.openLink(url);
  };

  return (
    <footer className="flex flex-col items-center gap-1 mt-10">
      {documents.map((doc) => (
        <a
          key={doc.label}
          className={linkClassName}
          href={doc.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={siteFooter ? undefined : openInTelegram(doc.url)}
        >
          {t(doc.label)}
        </a>
      ))}
      {siteFooter && (
        <Link className={linkClassName} href={localize('/wiki')} prefetch={false}>
          {t('wiki')}
        </Link>
      )}
      {siteFooter && routeIndexedLanguages('/free-skins')?.includes(lang) && (
        <Link className={linkClassName} href={localize('/free-skins')} prefetch={false}>
          {t('freeSkin')}
        </Link>
      )}
      {siteFooter &&
        SITE_PAGES.filter((page) => !isClosedForHost(page.path, host)).map((page) => (
          <Link key={page.path} className={linkClassName} href={localize(page.path)} prefetch={false}>
            {t(page.label)}
          </Link>
        ))}
      {isUz && (
        <div className="flex items-center justify-center gap-2 mt-3">
          <span className="inline-flex items-center rounded-md bg-white px-2 py-1">
            <img src="/paylogos/uzcard.png" alt="UzCard" className="h-4 w-auto" />
          </span>
          <span className="inline-flex items-center rounded-md bg-white px-2 py-1">
            <img src="/paylogos/humo.png" alt="Humo" className="h-4 w-auto" />
          </span>
        </div>
      )}
    </footer>
  );
};

export const LegalSection = memo(LegalSectionComponent);
